import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Table, InputNumber, TimePicker, Button, Tag, Spin, message } from 'antd';
import moment from 'moment';
import { workerPaySettingService } from '@iso/services';

const TIME_FMT = 'HH:mm:ss';

// Per-worker pay rate + overtime thresholds. OT can kick in by hours/day,
// hours/week, and/or a time-of-day penalty; these drive the Timesheets OT and
// Estimated Cost. Owner/admin only (enforced server-side too).
const PaySettings = () => {
  const activePropertyId = useSelector((state) => state.property.activePropertyId);
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const load = () => {
    if (!activePropertyId) return;
    setLoading(true);
    workerPaySettingService
      .getList(activePropertyId)
      .then((res) => {
        const list = res?.workers || [];
        setRows(list);
        const map = {};
        list.forEach((w) => {
          map[w.user_id] = {
            pay_rate: w.pay_rate != null ? Number(w.pay_rate) : null,
            ot_daily_threshold: w.ot_daily_threshold != null ? Number(w.ot_daily_threshold) : null,
            ot_weekly_threshold: w.ot_weekly_threshold != null ? Number(w.ot_weekly_threshold) : 38,
            ot_multiplier: w.ot_multiplier != null ? Number(w.ot_multiplier) : 1.5,
            ot_after_time: w.ot_after_time ? moment(w.ot_after_time, TIME_FMT) : null,
          };
        });
        setEdits(map);
      })
      .catch(() => message.error('Could not load pay settings'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [activePropertyId]);

  const setField = (uid, field, value) =>
    setEdits((e) => ({ ...e, [uid]: { ...e[uid], [field]: value } }));

  const saveRow = (record) => {
    const v = edits[record.user_id] || {};
    setSavingId(record.user_id);
    workerPaySettingService
      .save({
        property_id: activePropertyId,
        user_id: record.user_id,
        pay_rate: v.pay_rate ?? '',
        ot_daily_threshold: v.ot_daily_threshold ?? '',
        ot_weekly_threshold: v.ot_weekly_threshold ?? '',
        ot_multiplier: v.ot_multiplier ?? '',
        ot_after_time: v.ot_after_time ? v.ot_after_time.format(TIME_FMT) : '',
      })
      .then((res) => {
        if (res && res.code === '0000') message.success(`Saved ${record.name}'s pay settings`);
        else message.error(res?.message || 'Could not save');
      })
      .catch(() => message.error('Could not save'))
      .finally(() => setSavingId(null));
  };

  const columns = [
    {
      title: 'Worker',
      key: 'worker',
      width: 170,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.name}</div>
          <Tag color={r.type === 'contractor' ? 'gold' : 'green'}>{r.type === 'contractor' ? 'Contractor' : 'Employee'}</Tag>
        </div>
      ),
    },
    {
      title: 'Pay rate ($/h)',
      key: 'pay_rate',
      width: 130,
      render: (_, r) => (
        <InputNumber
          min={0}
          step={0.5}
          style={{ width: '100%' }}
          value={edits[r.user_id]?.pay_rate}
          onChange={(v) => setField(r.user_id, 'pay_rate', v)}
          formatter={(val) => (val ? `$ ${val}` : '')}
          parser={(val) => (val || '').replace(/[^\d.]/g, '')}
        />
      ),
    },
    {
      title: 'OT after (hrs/day)',
      key: 'ot_daily',
      width: 140,
      render: (_, r) => (
        <InputNumber
          min={0}
          max={24}
          step={0.5}
          placeholder="off"
          style={{ width: '100%' }}
          value={edits[r.user_id]?.ot_daily_threshold}
          onChange={(v) => setField(r.user_id, 'ot_daily_threshold', v)}
        />
      ),
    },
    {
      title: 'OT after (hrs/week)',
      key: 'ot_weekly',
      width: 150,
      render: (_, r) => (
        <InputNumber
          min={0}
          step={1}
          style={{ width: '100%' }}
          value={edits[r.user_id]?.ot_weekly_threshold}
          onChange={(v) => setField(r.user_id, 'ot_weekly_threshold', v)}
        />
      ),
    },
    {
      title: 'OT after (time)',
      key: 'ot_time',
      width: 130,
      render: (_, r) => (
        <TimePicker
          format="HH:mm"
          minuteStep={15}
          placeholder="off"
          style={{ width: '100%' }}
          value={edits[r.user_id]?.ot_after_time}
          onChange={(v) => setField(r.user_id, 'ot_after_time', v)}
        />
      ),
    },
    {
      title: 'OT ×',
      key: 'ot_mult',
      width: 90,
      render: (_, r) => (
        <InputNumber
          min={1}
          step={0.1}
          style={{ width: '100%' }}
          value={edits[r.user_id]?.ot_multiplier}
          onChange={(v) => setField(r.user_id, 'ot_multiplier', v)}
        />
      ),
    },
    {
      title: '',
      key: 'save',
      width: 84,
      render: (_, r) => (
        <Button type="primary" size="small" loading={savingId === r.user_id} onClick={() => saveRow(r)}>
          Save
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ color: '#667085', fontSize: 13, marginBottom: 12 }}>
        Overtime is the greater of the daily and weekly overage (a time-of-day
        penalty counts toward the day's OT). Leave a field blank to turn that rule
        off. These drive Timesheets overtime and Estimated Cost.
      </div>
      <Spin spinning={loading}>
        <Table
          rowKey="user_id"
          columns={columns}
          dataSource={rows}
          pagination={false}
          size="small"
          locale={{ emptyText: 'No workers to configure' }}
        />
      </Spin>
    </div>
  );
};

export default PaySettings;
